/**
 * Pure workbook parsing, text normalization, and column mapping helpers.
 * Matching, validation, and operation planning live in importEngine.js,
 * shared by the wizard and the API.
 */

import { TARGET_FIELDS, FIELD_BY_KEY } from './importColumns.js';

/* ==================================================================
 * 1. TEXT NORMALIZATION
 * ================================================================== */

/** Arabic-Indic (٠-٩) and extended/Persian (۰-۹) digits → ASCII. */
export function toLatinDigits(value) {
  if (value == null) return '';
  return String(value)
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
}

/**
 * The one normalizer used for every Arabic comparison in this file —
 * headers, names, family keys. Deterministic, no scoring:
 *   - strips tashkeel/diacritics and tatweel (ـ)
 *   - أ إ آ ٱ → ا   ·   ة → ه   ·   ى → ي   ·   ؤ → و   ·   ئ → ي
 *   - Arabic-Indic digits → Latin
 *   - collapses ALL whitespace runs (incl. NBSP) to one space, trims
 *   - lowercases (harmless for Arabic, needed for Latin headers)
 *
 * This is what makes `"الرقم  الوطني"` (two spaces) and `"اسم الطالب "`
 * (trailing space) match their single-space aliases.
 */
export function normalizeArabic(value) {
  if (value == null) return '';
  return toLatinDigits(value)
    .replace(/[ً-ْٰـ]/g, '')  // tashkeel + tatweel
    .replace(/[أإآٱ]/g, 'ا') // أإآٱ → ا
    .replace(/ة/g, 'ه')                  // ة → ه
    .replace(/ى/g, 'ي')                  // ى → ي
    .replace(/ؤ/g, 'و')                  // ؤ → و
    .replace(/ئ/g, 'ي')                  // ئ → ي
    .replace(/[\s ​-‏]+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Header-specific normalize (same rules; named for intent at call sites). */
export const normalizeHeader = normalizeArabic;

/** Trim + collapse inner whitespace, WITHOUT the Arabic letter folding.
 *  Used for values we store (we keep the office's real spelling). */
export function cleanText(value) {
  if (value == null) return '';
  const s = toLatinDigits(value).replace(/[\s ]+/g, ' ').trim();
  return s;
}

/* ==================================================================
 * 2. VALUE COERCION (per KIND)
 * ================================================================== */

/** Values that mean "there is no phone", written in the real file where
 *  a phone number was expected. Kept as a note, never as a number. */
const NON_PHONE_WORDS = ['متوفي', 'متوفى', 'متوفية', 'شهيد', 'شهيدة', 'بلا', 'لا يوجد', 'لايوجد', 'مجهول'];

/**
 * A phone cell in the real data is rarely just a number. Observed:
 *   "+963956846559"              clean
 *   "الاخ +963956846559"         relation label BEFORE the number
 *   "+905526558532 الكفيل"       relation label AFTER the number
 *   "متوفي" / "شهيد"             no number at all (deceased/martyr)
 *
 * We keep the number and hand back the surrounding Arabic as `label`
 * so it can be preserved in notes instead of being thrown away.
 */
export function parsePhone(raw) {
  const text = cleanText(raw);
  if (!text) return { phone: '', label: '', status: 'empty' };

  const digitsMatch = text.match(/\+?\d[\d\s\-()]*/g);
  const label = text.replace(/\+?\d[\d\s\-()]*/g, ' ').replace(/\s+/g, ' ').trim();

  if (!digitsMatch) {
    const norm = normalizeArabic(text);
    const isKnownWord = NON_PHONE_WORDS.some((w) => norm === normalizeArabic(w) || norm.includes(normalizeArabic(w)));
    return { phone: '', label: text, status: isKnownWord ? 'not_a_phone' : 'unparseable' };
  }

  // Longest digit run wins — "05317769818 الأخ" and "+62895420166372 خالد"
  // both give the number, not a stray fragment of the label.
  const best = digitsMatch
    .map((m) => m.trim())
    .sort((a, b) => b.replace(/\D/g, '').length - a.replace(/\D/g, '').length)[0];

  const hadPlus = best.trim().startsWith('+');
  const digits = best.replace(/\D/g, '');
  if (!digits) return { phone: '', label: text, status: 'unparseable' };

  const phone = (hadPlus ? '+' : '') + digits;
  return {
    phone,
    label,
    status: digitsMatch.filter((m) => m.replace(/\D/g, '').length >= 7).length > 1
      ? 'multiple' : digits.length < 7 || digits.length > 15 ? 'too_short' : 'ok',
  };
}

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);

/**
 * Dates arrive in three shapes in the real file:
 *   "10.4.2018"   d.m.yyyy   (the common one — 445 string cells)
 *   "27/8/2019"   d/m/yyyy   (21 cells)
 *   45886         Excel serial (numeric — all 621 registration dates)
 * All become 'YYYY-MM-DD', which is what the schema stores (§5 of
 * 0001_initial_schema.sql).
 */
export function parseDate(raw) {
  if (raw == null || raw === '') return { date: '', status: 'empty' };

  // Excel serial (number, or a numeric string that is clearly a serial)
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw < 1 || raw > 80000) return { date: '', status: 'out_of_range' };
    const ms = EXCEL_EPOCH_MS + Math.round(raw) * 86400000;
    return { date: isoFromMs(ms), status: 'ok' };
  }

  const text = toLatinDigits(raw).trim();
  if (!text) return { date: '', status: 'empty' };

  // Already ISO
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return finishDate(+iso[1], +iso[2], +iso[3]);

  // d.m.yyyy  /  d/m/yyyy  /  d-m-yyyy
  const dmy = text.match(/^(\d{1,2})\s*[./\\-]\s*(\d{1,2})\s*[./\\-]\s*(\d{4})$/);
  if (dmy) return finishDate(+dmy[3], +dmy[2], +dmy[1]);

  // A bare number in a text cell is still an Excel serial.
  if (/^\d+$/.test(text)) {
    const n = Number(text);
    if (n >= 1 && n <= 80000) {
      return { date: isoFromMs(EXCEL_EPOCH_MS + n * 86400000), status: 'ok' };
    }
  }

  return { date: '', status: 'unrecognized' };
}

function isoFromMs(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

function finishDate(y, m, d) {
  if (m < 1 || m > 12 || d < 1 || d > 31) return { date: '', status: 'impossible' };
  if (y < 1900 || y > 2100) return { date: '', status: 'impossible' };
  const dt = new Date(Date.UTC(y, m - 1, d));
  // Rejects 31.2.2018 and friends — JS would roll them into March.
  if (dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) {
    return { date: '', status: 'impossible' };
  }
  return { date: dt.toISOString().slice(0, 10), status: 'ok' };
}

/**
 * National id. In the real file this column is a mess: 234 numeric
 * cells, 157 text cells, 300 empty, and some cells hold a document type
 * ("دفتر عائلة") or "بلا" instead of a number. We keep digits only and
 * report the shape; an 11-digit value is the expected Syrian national id.
 */
export function parseNationalId(raw) {
  if (raw == null || raw === '') return { value: '', status: 'empty' };
  const text = toLatinDigits(raw).trim();
  const digits = text.replace(/\D/g, '');
  if (!digits) return { value: '', status: 'not_numeric', original: text };
  if (digits.length === 11) return { value: digits, status: 'ok' };
  return { value: digits, status: 'wrong_length', original: text };
}

/**
 * The national id as the school keeps it: 11 digits. Excel stores the
 * column as a number and drops the leading zeros (one, two or three of
 * them), so 8–10 digits are padded back. Anything else is not an id.
 */
export function fixNationalId(raw) {
  const digits = toLatinDigits(raw).replace(/[\s-]/g, '');
  return /^\d{8,11}$/.test(digits) ? digits.padStart(11, '0') : '';
}

const MALE_WORDS = ['ذكر', 'ذ', 'male', 'm', 'ولد'];
const FEMALE_WORDS = ['انثى', 'أنثى', 'ا', 'female', 'f', 'بنت', 'انثي'];

/** ذكر/انثى → 'male'/'female' (the CHECK constraint on students.gender). */
export function parseGender(raw) {
  const n = normalizeArabic(raw);
  if (!n) return { value: '', status: 'empty' };
  if (MALE_WORDS.some((w) => normalizeArabic(w) === n)) return { value: 'male', status: 'ok' };
  if (FEMALE_WORDS.some((w) => normalizeArabic(w) === n)) return { value: 'female', status: 'ok' };
  return { value: '', status: 'unrecognized', original: cleanText(raw) };
}

/* ==================================================================
 * 3. WORKBOOK PARSING
 *
 * Takes an already-read SheetJS workbook (the component does the file
 * reading; this stays pure) and returns plain arrays.
 * ================================================================== */

/**
 * Describe every sheet: name, header row, row count, and whether it is
 * worth importing. The real file's "ورقة1" has no !ref at all — it is
 * reported as empty and excluded from the default selection rather than
 * silently producing zero rows later.
 */
const HEADER_ALIASES = new Set(TARGET_FIELDS.flatMap((f) => f.aliases.map(normalizeHeader)));

export function readWorkbook(workbook, XLSX, headerRows = {}) {
  const sheets = (workbook.SheetNames || []).map((name) => {
    const ws = workbook.Sheets[name];
    if (!ws || !ws['!ref']) {
      return { name, headers: [], rows: [], rowCount: 0, empty: true };
    }
    // Formatting can extend !ref to XFD (16,384 columns). Visit actual values
    // only, preserving their Excel coordinates rather than allocating padding.
    const content = new Map();
    for (const address of Object.keys(ws)) {
      if (!/^[A-Z]+\d+$/.test(address)) continue;
      const value = ws[address]?.v;
      if (value == null || String(value).trim() === '') continue;
      const cell = XLSX.utils.decode_cell(address);
      if (!content.has(cell.r)) content.set(cell.r, new Map());
      content.get(cell.r).set(cell.c, value);
    }
    if (!content.size) return { name, headers: [], rows: [], rowCount: 0, empty: true };
    let detected = 0, score = 0;
    for (const [r, cells] of content) {
      if (r >= 50) continue;
      const hits = [...cells.values()].filter((v) => HEADER_ALIASES.has(normalizeHeader(v))).length;
      if (hits > score || (hits === score && r < detected)) { score = hits; detected = r; }
    }
    const headerRow = Math.max(1, Number(headerRows[name]) || (score >= 3 ? detected + 1 : 1));
    const headerCells = content.get(headerRow - 1) || new Map();
    // Import the table described by its headings. Register files also contain
    // accidentally repeated cells across the rest of the entire Excel row.
    const lastColumn = Math.max(0, ...headerCells.keys());
    const outsideTableCells = [...content.values()].reduce((n, cells) => n + [...cells.keys()].filter((c) => c > lastColumn).length, 0);
    const headers = Array.from({ length: lastColumn + 1 }, (_, c) => String(headerCells.get(c) ?? ''));
    const rows = [...content.entries()].filter(([r]) => r >= headerRow).sort(([a], [b]) => a - b)
      .map(([r, cells]) => ({ sheet: name, excelRow: r + 1, date1904: !!workbook.Workbook?.WBProps?.date1904,
        cells: headers.map((_, c) => cells.get(c) ?? null) }))
      .filter((row) => row.cells.some((cell) => cell != null && String(cell).trim() !== ''));
    return { name, headers, headerRow, outsideTableCells, rows, rowCount: rows.length, empty: rows.length === 0 };
  });

  return { sheets, sheetNames: sheets.map((s) => s.name) };
}

/* ==================================================================
 * 4. COLUMN MATCHING (deterministic — no AI, no fuzzy scoring)
 * ================================================================== */

/**
 * Auto-match headers to target fields.
 *
 * Pass 1  exact normalized equality against each field's aliases.
 * Pass 2  conflict resolution: when two headers claim the SAME field
 *         (the "اسم الطالب" case — column C and column N normalize
 *         identically), the header whose position equals the field's
 *         `preferIndex` keeps it; the loser is re-offered to any other
 *         field that lists its header as an alias and is still free.
 *
 * Returns { mapping, unmatchedHeaders, missingRequired, conflicts }
 * where `mapping` is { [columnIndex]: fieldKey }.
 */
export function autoMatchColumns(headers) {
  const normHeaders = headers.map((h) => normalizeHeader(h));

  // header index -> list of field keys whose aliases match it
  const candidates = normHeaders.map((nh) => {
    if (!nh) return [];
    return TARGET_FIELDS
      .filter((f) => f.aliases.some((a) => normalizeHeader(a) === nh))
      .map((f) => f.key);
  });

  const mapping = {};
  const takenFields = new Set();
  const conflicts = [];

  // --- Pass A: positional preference wins first.
  // A header at exactly the position a field expects is the strongest
  // deterministic signal available, and it is what separates the two
  // "اسم الطالب" columns.
  headers.forEach((_, idx) => {
    const keys = candidates[idx];
    if (!keys.length) return;
    const exactPos = keys.find((k) => FIELD_BY_KEY[k].preferIndex === idx && !takenFields.has(k));
    if (exactPos) {
      mapping[idx] = exactPos;
      takenFields.add(exactPos);
    }
  });

  // --- Pass B: everything still unassigned takes its first free candidate.
  headers.forEach((_, idx) => {
    if (mapping[idx]) return;
    const keys = candidates[idx];
    if (!keys.length) return;
    const free = keys.find((k) => !takenFields.has(k));
    if (free) {
      mapping[idx] = free;
      takenFields.add(free);
    } else {
      // Every field this header could be is already claimed by a
      // better-positioned header. Surface it rather than hide it.
      conflicts.push({
        headerIndex: idx,
        header: headers[idx],
        wanted: keys,
        takenBy: keys.map((k) => ({
          key: k,
          byIndex: Number(Object.keys(mapping).find((i) => mapping[i] === k)),
        })),
      });
    }
  });

  const unmatchedHeaders = headers
    .map((h, idx) => ({ index: idx, header: h }))
    .filter((h) => !mapping[h.index] && cleanText(h.header) !== '');

  const mappedKeys = new Set(Object.values(mapping));
  const missingRequired = TARGET_FIELDS
    .filter((f) => f.required && !mappedKeys.has(f.key))
    .map((f) => f.key);

  return { mapping, unmatchedHeaders, missingRequired, conflicts };
}

/** Invert a mapping to { fieldKey: columnIndex } for row extraction. */
export function invertMapping(mapping) {
  const out = {};
  for (const [idx, key] of Object.entries(mapping)) {
    if (key) out[key] = Number(idx);
  }
  return out;
}
